import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref } from 'vue'

import { cloneDeep } from 'lodash'

import api from '@app/insights/api'
import serialize from '@app/insights/utils/serialize'
import { periodSelectionTypes } from '@app/insights/types'
import I18n from '@app/adapters/I18n'
import { SfLog } from '@app/adapters/logger'
import { useFiltersStore } from '@app/insights/store/filters'
import { useInsightsRootStore } from '@app/insights/store/insights-root'
import { useCompaniesStore } from '@app/insights/store/companies'
import { useSegmentsStore } from '@app/insights/store/segments'
import { useColumnsStore } from '@app/insights/store/columns'
import type { ActiveFilter } from '@app/insights/store/filters/types'

import { createBusSubscription } from './bus-subscription'

export type AssistantRole = 'user' | 'assistant'

export interface AssistantMessage {
  id: string
  role: AssistantRole
  text: string
  // Suggested follow-up prompts. Only ever populated on assistant messages,
  // and only the most recent assistant message renders them as pills.
  suggestions: string[]
  // Snapshot of the table state as it stood right after this reply, so "Rewind"
  // can restore it. `tableSnapshot` is the active filters (= columns) and
  // `suggestedColumns` the AI-highlighted column uuids at that point.
  tableSnapshot: ActiveFilter[]
  suggestedColumns: string[]
}

// Payloads published by Insights::AgentRunnerJob over MessageBus.
type AgentPayload = {
  conversation_id: number
  kind: 'progress' | 'delta' | 'text' | 'filters' | 'error'
  tool?: string
  text?: string
  message?: string
  explanation?: string
  filters?: unknown[]
  ledger_select?: Record<string, unknown>
  segment?: {
    action: 'selected' | 'saved'
    id: number
    partner_segment_id?: number | null
    name?: string
  }
  sort?: { column: string; direction: string }
  follow_ups?: string[]
}

type ConversationStatus = 'idle' | 'running' | 'failed'

// Shapes returned by the agent conversations API (snake_case, un-camelized).
interface AgentTurnResponse {
  id: number
  message_bus_channel: string
  message_bus_last_id: number
}

interface AgentConversationState {
  status: ConversationStatus
  latest_result: AgentPayload | null
}

// A single row of the on-screen Insights table, as exposed by the companies store.
interface CompanyRow {
  id: number
  filter_values: Record<string, { formatted_value?: unknown }>
}

// Rows sampled from the on-screen table into the agent's view context.
const CONTEXT_SAMPLE_SIZE = 10

let messageSeq = 0
function nextMessageId(): string {
  messageSeq += 1
  return `assistant-msg-${messageSeq}`
}

export const useAssistantStore = defineStore('insights-assistant', () => {
  const filtersStore = useFiltersStore()

  const conversationId = ref<number | null>(null)
  const messages = ref<AssistantMessage[]>([])
  const draft = ref<string>('')
  const loading = ref<boolean>(false)
  // Cumulative snapshot of the reply being generated, streamed over MessageBus.
  const streamingText = ref<string>('')
  const currentTool = ref<string | null>(null)
  // Column uuids the assistant currently proposes; the sf-ai header highlight
  // reads from here.
  const suggestedColumnNames = ref<Set<string>>(new Set())

  // The agent turn channel, subscribed on the first turn and reused across every
  // conversation and turn for the store's lifetime.
  const turnSubscription = createBusSubscription(handleAgentPayload)

  // MessageBus delivery is best-effort: a dropped terminal message would otherwise leave
  // the turn loading forever (the reply and its side effects never arrive). While a turn is
  // loading, this poll reconciles against the authoritative server state and, once the turn
  // is no longer running, concludes it from the persisted latest_result. finishReply's loading
  // guard makes whichever of MessageBus / this poll resolves first win; the other is a no-op.
  const RECONCILE_INTERVAL_MS = 4000
  let reconcileTimer: ReturnType<typeof setInterval> | null = null
  let reconcileInFlight = false

  function stopReconcile(): void {
    if (reconcileTimer) {
      clearInterval(reconcileTimer)
      reconcileTimer = null
    }
  }

  function startReconcile(): void {
    stopReconcile()
    reconcileTimer = setInterval(
      () => void reconcileTurn(),
      RECONCILE_INTERVAL_MS
    )
  }

  async function reconcileTurn(): Promise<void> {
    if (!loading.value || conversationId.value === null || reconcileInFlight)
      return

    // Pin the id we are reconciling: a conversation switch (new chat / open another) during the
    // in-flight fetch must not apply this conversation's result to whatever is now active.
    const reconcilingId = conversationId.value
    reconcileInFlight = true
    try {
      const conv = (await api.fetchAgentConversation(
        reconcilingId
      )) as AgentConversationState
      // A "running" status means the reply is genuinely still in flight; only conclude once
      // the server reports the turn finished and has a result to replay — and only if we are
      // still on the same conversation we fetched.
      if (
        loading.value &&
        conversationId.value === reconcilingId &&
        conv.status !== 'running' &&
        conv.latest_result
      ) {
        handleAgentPayload({
          ...conv.latest_result,
          conversation_id: reconcilingId,
        })
      }
    } catch (error) {
      SfLog.error('Insights assistant reconcile poll failed', { error })
    } finally {
      reconcileInFlight = false
    }
  }

  const hasConversation = computed(() => messages.value.length > 0)

  // Pills only show under the assistant's latest reply (ticket requirement).
  const lastAssistantMessageId = computed(() => {
    for (let i = messages.value.length - 1; i >= 0; i--) {
      if (messages.value[i].role === 'assistant') return messages.value[i].id
    }
    return null
  })

  // The disclaimer under the table is shown once the assistant has proposed at
  // least one table state.
  const tableModifiedByAssistant = computed(() =>
    messages.value.some((message) => message.role === 'assistant')
  )

  function isColumnSuggested(columnName: string): boolean {
    return suggestedColumnNames.value.has(columnName)
  }

  function setDraft(value: string): void {
    draft.value = value
  }

  function addMessage(message: Omit<AssistantMessage, 'id'>): AssistantMessage {
    const entry: AssistantMessage = { id: nextMessageId(), ...message }
    messages.value.push(entry)
    return entry
  }

  // The turn subscription is reused across conversations, so cancel/startNew must not tear
  // it down. Only disposal does, or the MessageBus handler outlives the app.
  onScopeDispose(() => {
    stopReconcile()
    turnSubscription.dispose()
  })

  // Stop / Esc / new chat while pending: the turn's job keeps running server-side,
  // but its payloads are dropped on arrival (see the loading guard in finishReply).
  function cancel(): void {
    loading.value = false
    streamingText.value = ''
    currentTool.value = null
    stopReconcile()
  }

  async function submitPrompt(prompt?: string): Promise<void> {
    const text = (prompt ?? draft.value).trim()
    if (!text || loading.value) return

    addMessage({
      role: 'user',
      text,
      suggestions: [],
      suggestedColumns: [],
      tableSnapshot: [],
    })
    draft.value = ''
    loading.value = true
    streamingText.value = ''
    currentTool.value = null

    try {
      const context = currentViewContext()
      const response = (
        conversationId.value
          ? await api.sendAgentMessage(conversationId.value, text, context)
          : await api.createAgentConversation(text, context)
      ) as AgentTurnResponse
      conversationId.value = response.id
      // Subscribe only after the turn is enqueued, on the channel the server named and from
      // the position it captured before the job could publish (message_bus_last_id, NOT -1:
      // the MessageBus client can register after the job already streamed its updates, and
      // replaying from lastId delivers them regardless of registration timing). Only the
      // first turn subscribes; later turns publish at higher ids the existing subscription
      // already receives.
      turnSubscription.subscribe(
        response.message_bus_channel,
        response.message_bus_last_id ?? 0
      )
      // Safety net in case the terminal MessageBus message is dropped (see reconcileTurn).
      startReconcile()
    } catch (error) {
      SfLog.error('Insights assistant request failed', { error })
      finishReply(I18n.t('companies_overview.assistant.request_failed'))
    }
  }

  // Suggestion pills (both empty-state and reply) submit immediately; we do not
  // wait for a second user action.
  function submitSuggestion(suggestion: string): void {
    void submitPrompt(suggestion)
  }

  // Snapshot of what the user currently sees, sent with every message so the
  // agent can modify the current filters and reason about on-screen results.
  function currentViewContext(): Record<string, unknown> {
    const rootStore = useInsightsRootStore()
    const segmentsStore = useSegmentsStore()
    const companiesStore = useCompaniesStore()
    const columnsStore = useColumnsStore()

    const activeFilters = filtersStore.getActiveFilters
    const rows = companiesStore.sortedAndFilteredCompanies as CompanyRow[]

    return {
      segment: segmentsStore.segment
        ? {
            id: segmentsStore.segment.id,
            partner_segment_id: segmentsStore.segment.partner_segment_id,
            name: segmentsStore.segment.name,
          }
        : null,
      filters: serialize.filterSettings(
        activeFilters,
        filtersStore.allFiltersByType,
        { includeId: false }
      ),
      ledger_select: serialize.ledgerSelectSettings({
        periodSelection: rootStore.periodSelection,
        ledgerSelectDate: rootStore.ledgerSelectDate,
        ledgerSelectLockType: rootStore.ledgerSelectLockType,
      }),
      sort: {
        column: columnsStore.sortColumn,
        direction: columnsStore.sortType,
      },
      results: {
        total_on_screen: rows.length,
        sample: rows.slice(0, CONTEXT_SAMPLE_SIZE).map((row) => ({
          company_id: row.id,
          name: row.filter_values?.name?.formatted_value,
          values: Object.fromEntries(
            activeFilters.map((filter) => [
              filter.uuid,
              row.filter_values?.[filter.uuid]?.formatted_value,
            ])
          ),
        })),
      },
    }
  }

  function handleAgentPayload(payload: AgentPayload): void {
    if (payload.conversation_id !== conversationId.value) return

    switch (payload.kind) {
      case 'progress':
        if (payload.tool) currentTool.value = payload.tool
        break
      case 'delta':
        // Cumulative snapshot of the answer being generated
        streamingText.value = payload.text ?? ''
        break
      case 'text':
        void concludeTurn(payload, payload.text ?? '')
        break
      case 'filters':
        void concludeTurn(payload, payload.explanation ?? '')
        break
      case 'error':
        finishReply(
          payload.message ??
            I18n.t('companies_overview.assistant.request_failed')
        )
        break
      default:
        // The backend should only send the kinds handled above; surface anything else so a
        // malformed payload is visible instead of being silently dropped.
        SfLog.warn('Insights assistant received an unknown payload kind', {
          extra: { payload },
        })
    }
  }

  // Side effects must land before the reply is appended, so the reply's table
  // snapshot (used by Rewind) reflects the state the assistant produced.
  async function concludeTurn(
    payload: AgentPayload,
    text: string
  ): Promise<void> {
    // A terminal payload that arrives after the turn already ended (cancelled, or a
    // MessageBus message that lost the race to the reconcile poll) must not re-apply side
    // effects or append a duplicate reply. Claim the turn synchronously here, BEFORE the
    // applySideEffects await, so a second concludeTurn racing during that await sees
    // loading=false and bails — otherwise both would apply side effects.
    if (!loading.value) return
    loading.value = false

    // Side effects (segment load, filter run) can fail independently of the reply itself;
    // still surface the answer rather than dropping it silently, and record why the table
    // may not reflect what the reply describes.
    try {
      await applySideEffects(payload)
    } catch (error) {
      SfLog.error('Insights assistant failed to apply turn side effects', {
        error,
      })
    }
    appendReply(text, payload)
  }

  // Segment selection/saving loads the segment (which resets filters), so it must
  // complete before agent-provided filters are applied on top.
  async function applySideEffects(payload: AgentPayload): Promise<void> {
    const segmentsStore = useSegmentsStore()

    if (payload.segment) {
      if (payload.segment.action === 'saved')
        await segmentsStore.fetchSegments()
      await segmentsStore.loadSegment({
        id: payload.segment.id,
        partner_segment_id: payload.segment.partner_segment_id ?? undefined,
      })
    }

    if (payload.kind === 'filters' || payload.filters?.length) {
      applyAgentFilters(payload)
    }

    if (payload.sort) {
      useColumnsStore().setSortAndType({
        sortColumn: payload.sort.column,
        sortType: payload.sort.direction,
      })
    }
  }

  function applyAgentFilters(payload: AgentPayload): void {
    const rootStore = useInsightsRootStore()
    const segmentsStore = useSegmentsStore()
    const ledgerSelect = payload.ledger_select ?? {}

    // Agent-built filters are an ad-hoc query: detach from the selected segment,
    // otherwise its last request_ref serves the cached run computed with the OLD
    // filters and new columns never receive data (and the fresh run would be
    // persisted onto the saved segment).
    if (segmentsStore.segment) segmentsStore.unselectSegment()

    Object.values(periodSelectionTypes).forEach((periodType) => {
      if (ledgerSelect[periodType]) rootStore.setPeriodSelection(periodType)
    })
    if (typeof ledgerSelect.date === 'string') {
      rootStore.setLedgerSelectDate(ledgerSelect.date)
    }
    if (typeof ledgerSelect.locking_type === 'string') {
      rootStore.setLedgerSelectLockType(ledgerSelect.locking_type)
    }

    filtersStore.setActiveFilters(payload.filters ?? [])
    // The whole table state is assistant-built at this point: highlight every
    // applied column (setActiveFilters keeps the agent's cached_uuid as uuid).
    suggestedColumnNames.value = new Set(
      filtersStore.getActiveFilters.map((filter) => filter.uuid)
    )
    void useCompaniesStore().fetchFilteredCompanies()
  }

  // Ends the loading state and appends the reply. A turn that already ended (cancelled via
  // Stop / Esc, or concluded by the racing MessageBus / reconcile path) has loading off, so
  // its late payload is dropped. concludeTurn claims the turn itself and calls appendReply
  // directly, so this guarded entry point is for the error/failure paths.
  function finishReply(text: string, payload?: AgentPayload): void {
    if (!loading.value) return

    loading.value = false
    appendReply(text, payload)
  }

  // Appends the assistant reply and clears the per-turn UI state. Assumes the caller has
  // already claimed the turn (set loading false); never guards on loading itself.
  function appendReply(text: string, payload?: AgentPayload): void {
    addMessage({
      role: 'assistant',
      text,
      suggestions: payload?.follow_ups ?? [],
      // Capture the table state as it stands now so Rewind can restore it.
      tableSnapshot: cloneDeep(filtersStore.getActiveFilters),
      suggestedColumns: [...suggestedColumnNames.value],
    })
    streamingText.value = ''
    currentTool.value = null
    stopReconcile()
  }

  // Rewinds the table to the state reflected in the given assistant reply:
  // restores the columns (active filters) and the AI highlight from its
  // snapshot. The filters watcher then re-runs the query for that state.
  function rewindTo(messageId: string): void {
    const target = messages.value.find((message) => message.id === messageId)
    if (!target || target.role !== 'assistant') return

    filtersStore.restoreActiveFilters(target.tableSnapshot)
    suggestedColumnNames.value = new Set(target.suggestedColumns)
  }

  // Drops the panel state; the next message starts a new conversation. Late
  // payloads for the old conversation no longer match conversationId and are
  // ignored. The table's columns stay; only the AI highlight is cleared.
  function startNew(): void {
    cancel()
    conversationId.value = null
    messages.value = []
    draft.value = ''
    suggestedColumnNames.value = new Set()
  }

  return {
    conversationId,
    messages,
    draft,
    loading,
    streamingText,
    currentTool,
    hasConversation,
    lastAssistantMessageId,
    tableModifiedByAssistant,
    isColumnSuggested,
    setDraft,
    submitPrompt,
    submitSuggestion,
    rewindTo,
    cancel,
    startNew,
  }
})
